import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Marker } from 'react-map-gl';

import { format } from 'date-fns';
import { ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MapBox } from '@/components/ui/map';
import { env } from '@/constants/env';
import type { Estate } from '@/features/estates-property/api/estate';
import { EstateImage } from '@/features/estates-property/components/estate-image';
import { getFloorLabel } from '@/features/estates-property/utils/floorLabelHandler';
import { cn } from '@/lib/utils';
import { getRenderValue } from '@/utils/getEmptyVisualValue';
import type { Currency } from '@/utils/getPriceWithCurrency';
import { getPriceWithCurrency } from '@/utils/getPriceWithCurrency';

import type { EstateAnalysis } from '../api/analytics';
import type { OrderBy } from '../api/offer-search';
import { useOfferSearch } from '../api/offer-search';
import { NetLivingAreaFilter } from './net-living-area-filter';
import { NumberOfRoomsFilter } from './number-of-rooms-filter';
import { OfferSearchFilter } from './offer-search-filter';

const MAP_BOX_TOKEN = env.MAP_BOX_API_TOKEN;

type SortOption = {
  label: string;
  value: OrderBy;
};

export const OfferSearch = ({
  data: estate,
  estateAnalysis,
}: {
  data: Estate;
  estateAnalysis: EstateAnalysis;
}) => {
  const { t } = useTranslation();

  const sortOptions = useMemo<SortOption[]>(
    () => [
      {
        label: t('distanceClosest'),
        value: {
          field: 'distance',
          direction: 'asc',
        },
      },
      {
        label: t('distanceFurthest'),
        value: {
          field: 'distance',
          direction: 'desc',
        },
      },
      {
        label: t('priceHighest'),
        value: {
          field: 'salePrice',
          direction: 'desc',
        },
      },
      {
        label: t('priceLowest'),
        value: {
          field: 'salePrice',
          direction: 'asc',
        },
      },
      {
        label: t('pricePerM2Highest'),
        value: {
          field: 'salePricePerSqm',
          direction: 'desc',
        },
      },
      {
        label: t('pricePerM2Lowest'),
        value: {
          field: 'salePricePerSqm',
          direction: 'asc',
        },
      },
      {
        label: t('dateRecent'),
        value: {
          field: 'startDate',
          direction: 'asc',
        },
      },
      {
        label: t('dateOldest'),
        value: {
          field: 'startDate',
          direction: 'desc',
        },
      },
      {
        label: t('daysOnMarketMost'),
        value: {
          field: 'daysOnMarket',
          direction: 'desc',
        },
      },
      {
        label: t('daysOnMarketFewest'),
        value: {
          field: 'daysOnMarket',
          direction: 'asc',
        },
      },
    ],
    [t]
  );

  const statusOptions = useMemo<{ label: string; value: string }[]>(
    () => [
      {
        label: t('active'),
        value: 'active',
      },
      {
        label: t('historic'),
        value: 'historic',
      },
    ],
    [t]
  );

  const lastUpdateOptions = useMemo<{ label: string; value: number }[]>(
    () => [
      {
        label: t('lastXDays', { days: 15 }),
        value: 15,
      },
      {
        label: t('lastXDays', { days: 30 }),
        value: 30,
      },
      {
        label: t('lastYear'),
        value: 365,
      },
      {
        label: t('lastXYears', { years: 2 }),
        value: 2 * 365,
      },
      {
        label: t('lastXYears', { years: 5 }),
        value: 5 * 365,
      },
      {
        label: t('allTime'),
        value: 0,
      },
    ],
    [t]
  );

  const [orderBy, setOrderBy] = useState(sortOptions[0]);
  const [statuses, setStatuses] = useState(statusOptions);
  const [lastUpdate, setLastUpdate] = useState(lastUpdateOptions[2]);
  const [filters, setFilters] = useState<Record<string, { min: number; max: number } | undefined>>({
    livingArea: undefined,
    numberOfRooms: undefined,
  });

  const statusFilter = useMemo(() => {
    if (statusOptions.length === statuses.length) {
      return undefined; // all
    }
    return statuses.map(({ value }) => value);
  }, [statusOptions, statuses]);

  const { data } = useOfferSearch(estateAnalysis, orderBy.value, {
    ...filters,
    isActive: statusFilter,
    startDate: { min: lastUpdate.value, max: 0 },
  });

  const offerMarkers = useMemo(() => {
    const markers: JSX.Element[] = [];
    const offset = 0.0001;
    const sameCoordinatesMap: Record<string, number> = {};
    data.forEach((offer, idx) => {
      if (offer.coordinates) {
        const key = `${offer.coordinates.latitude}_${offer.coordinates.longitude}`;
        const times = sameCoordinatesMap[key] ?? 0;
        sameCoordinatesMap[key] = times + 1;
        markers.push(
          <Marker
            key={offer.offerId}
            latitude={offer.coordinates.latitude + offset * times}
            longitude={offer.coordinates.longitude + offset * times}
            anchor="bottom"
          >
            <div className="relative flex flex-col items-center">
              <div className="flex h-6 w-6 items-center justify-center rounded bg-primary-light text-xs font-semibold text-white shadow-md">
                {String.fromCharCode(65 + idx)}
              </div>
              <div className="h-0 w-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-primary-light" />
            </div>
          </Marker>
        );
      }
    });
    return markers;
  }, [data]);

  const mapAnchors = useMemo(() => {
    const anchors = [`pin-s-x+000000(${estate?.address?.long},${estate?.address?.lat})`];
    // show 5 places on map
    data.slice(0, 5).forEach((marker, idx) => {
      if (marker.coordinates) {
        anchors.push(
          `pin-s-${String.fromCharCode(65 + idx).toLowerCase()}+07b4c0(${marker.coordinates.longitude},${marker.coordinates.latitude})`
        );
      }
    });
    return anchors;
  }, [estate, data]);

  return (
    <div className="print:-mx-8">
      <h2 className="mb-6 text-md font-bold text-primary-dark print:hidden">{t('offerSearch')}</h2>
      <div className="grid grid-cols-[675px_1fr] gap-6 max-lg:block max-lg:space-y-6 print:flex print:flex-col print:gap-2">
        {/* Map */}
        <Card className="print:hidden print:p-0">
          <div className="mb-2 p-6 print:hidden">
            <h3 className="text-md font-bold text-primary-dark">{t('map')} </h3>
            <p className="text-sm text-text-secondary">{estate?.address?.city}</p>
          </div>
          <div className="h-[455px]">
            <MapBox
              zoom={12}
              latitude={Number(estate?.address?.lat)}
              longitude={Number(estate?.address?.long)}
            >
              {offerMarkers}
            </MapBox>
          </div>
        </Card>
        {/* Offers */}
        <Card className="p-6 print:p-0 print:shadow-none">
          <div className="mb-4 flex gap-2 print:hidden">
            <h3 className="text-md font-bold text-primary-dark">{t('offers')} </h3>
            <div className="flex flex-1 flex-wrap justify-end gap-2">
              <OfferSearchFilter
                label={t('status')}
                options={statusOptions}
                multiple={true}
                value={statuses}
                onChange={setStatuses}
              />
              <OfferSearchFilter
                label={lastUpdate.label}
                options={lastUpdateOptions}
                multiple={false}
                value={lastUpdate}
                onChange={setLastUpdate}
              />
              <NetLivingAreaFilter
                livingArea={filters.livingArea}
                onValueChange={(livingArea) =>
                  setFilters((prevFilters) => ({ ...prevFilters, livingArea }))
                }
              />
              <NumberOfRoomsFilter
                numberOfRooms={filters.numberOfRooms}
                onValueChange={(numberOfRooms) =>
                  setFilters((prevFilters) => ({ ...prevFilters, numberOfRooms }))
                }
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline">
                    <span>{orderBy.label}</span>
                    <ChevronDown className="ml-1" aria-hidden />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {sortOptions.map((option) => (
                    <DropdownMenuItem
                      key={`${option.value.field}_${option.value.direction}`}
                      onClick={() => setOrderBy(option)}
                    >
                      <span>{option.label}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <div className="max-h-[460px] space-y-2 overflow-y-auto print:max-h-max print:overflow-y-hidden">
            <div className="sticky left-0 top-0 z-20 grid grid-cols-4 gap-x-2 bg-white text-sm text-text-secondary">
              <div className="col-span-2 flex gap-x-2">
                <div className='className="h-16 rounded-md" w-24' />
                <div>{t('name')}</div>
              </div>
              <div>{t('price')}</div>
              <div>{t('details')}</div>
            </div>
            {data.length === 0 ? (
              <p className="!my-20 text-center text-sm text-text-secondary">{t('noOffers')}</p>
            ) : (
              data.map((estate, idx) => {
                return (
                  <div
                    key={estate.offerId}
                    className={cn(
                      'grid grid-cols-4 gap-x-2 border-b pb-2',
                      idx > 5 && 'print:hidden'
                    )}
                  >
                    <div className="col-span-2 flex gap-x-2">
                      <div className="relative">
                        <div className="t-0 l-0 absolute z-10 flex h-6 w-6 items-center justify-center rounded-tl-md bg-primary-light text-sm text-white">
                          {String.fromCharCode(65 + idx)}
                        </div>
                        <EstateImage
                          className="h-14 w-20 rounded-md print:h-12 print:w-16 [&_img]:rounded-md"
                          {...(estate.images?.length && { url: estate.images[0].url })}
                        />
                      </div>
                      <div className="flex-1 overflow-hidden whitespace-normal 2xl:whitespace-nowrap">
                        <p className="overflow-hidden text-ellipsis text-base font-bold leading-normal print:text-sm print:font-bold print:leading-tight">
                          {estate.title}
                        </p>
                        <p className="text-sm font-normal leading-normal print:text-xs">
                          {estate.address?.city}, {estate.address?.postCode}
                        </p>
                        <p className="text-sm font-normal leading-normal text-text-secondary print:text-xs">
                          {estate.isActive ? t('active') : t('historic')}
                        </p>
                      </div>
                    </div>
                    <div>
                      <p className="text-base font-bold leading-normal print:text-xs print:font-bold print:leading-tight">
                        {getPriceWithCurrency(estate.salePrice, estate.currency as Currency)} /{' '}
                        {getPriceWithCurrency(estate.salePricePerSqm, estate.currency as Currency)}
                        -m
                        <sup>2</sup>
                      </p>
                      <p className="text-sm font-normal leading-normal print:text-xs">
                        {getRenderValue(estate.daysOnMarket)} {t('daysOnMarket')}
                      </p>
                      <p className="text-sm font-normal leading-normal print:text-xs">
                        (
                        <span className="whitespace-nowrap">
                          {estate.startDate ? format(estate.startDate, 'yyyy.MM.dd') : '-'}
                        </span>
                        -
                        <span className="whitespace-nowrap">
                          {estate.endDate ? format(estate.endDate, 'yyyy.MM.dd') : '-'}
                        </span>
                        )
                      </p>
                    </div>
                    <div className="grid grid-cols-1 gap-2 2xl:grid-cols-2 print:grid-cols-2 print:gap-0.5">
                      <div className="h-6 overflow-hidden text-ellipsis whitespace-nowrap rounded bg-[#07B1BE40] px-2 text-center text-sm text-primary-light">
                        {getRenderValue(estate.livingArea)} m<sup>2</sup>
                      </div>
                      <div className="h-6 overflow-hidden text-ellipsis whitespace-nowrap rounded bg-[#07B1BE40] px-2 text-center text-sm text-primary-light">
                        {getFloorLabel(estate.floorNumber ? Number(estate.floorNumber) : 0)}
                      </div>
                      <div className="h-6 overflow-hidden text-ellipsis whitespace-nowrap rounded bg-[#07B1BE40] px-2 text-center text-sm text-primary-light">
                        {getRenderValue(estate.numberOfRooms)} {t('totalNumberOfRooms')}
                      </div>
                      <div className="h-6 overflow-hidden text-ellipsis whitespace-nowrap rounded bg-[#07B1BE40] px-2 text-center text-sm text-primary-light">
                        ---- {t('numberOfBathrooms')}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Card>
        {/* Map in print mode */}
        <img
          src={`https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/${mapAnchors.join(',')}/${estate?.address?.long},${estate?.address?.lat},12,0,0/1000x540?access_token=${MAP_BOX_TOKEN}`}
          alt="Static map"
          className="hidden rounded-md print:block"
        />
      </div>
    </div>
  );
};
